<div class="header-bar flex justify-content-between align-items-center">
  <h1>Job Hold Configuration</h1>
  <button
    pButton
    class="ui-button-primary"
    [icon]="saving ? 'fa fa-spin fa-circle-o-notch' : 'fa ui-icon-check'"
    label="Save"
    (click)="confirmSave()"
    [disabled]="saveDisabledReason"
    [pTooltip]="saveDisabledReason"
    style="width: 150px"
  ></button>
</div>

<div class="content-container">
  <div *ngIf="isLoading && !settings" class="flex justify-content-center my-4">
    <i class="fa fa-spin fa-spinner fa-3x"></i>
  </div>

  <ng-container *ngIf="settings">
    <app-alert-panel
      severity="info"
      heading="How the release time is worked out"
      class="block mb-3"
    >
      <div class="flex justify-content-between align-items-start grid-gap-10">
        <p class="mt-0">
          A new job is held, so suppliers can't see it, until its release time. The release time is
          worked out in the steps below, and each step has its own section on this page.
        </p>
        <button
          pButton
          type="button"
          class="ui-button-secondary"
          [label]="showGuide ? 'Hide' : 'Show'"
          (click)="showGuide = !showGuide"
        ></button>
      </div>

      <div *ngIf="showGuide">
        <ol>
          <li>
            <b>Short timer or release window.</b> The business days from now to the latest
            collection are counted. Weekends and bank holidays don't count, and a weekend collection
            counts as the Monday after. Fewer than <i>Business days for a release window</i> and the
            job gets the <b>Short timer</b>: it's held for a set number of minutes. Otherwise it
            uses the <b>Release windows</b> table: it's released on the release day for its
            collection day, at a time in that row's window.
          </li>
          <li>
            <b>Late release.</b> A job booked on or after its release day has missed its window, so
            it's released in the <b>Late release</b> window that day, and not before its minimum
            hold.
          </li>
          <li>
            <b>Overnight.</b> A window or late release that falls between the cutoff and the morning
            window is moved into the morning window. The short timer isn't moved.
          </li>
          <li>
            <b>Earliest collection.</b> Whatever the steps above give, the job comes out no later
            than this many minutes before its earliest collection. The same week setting is used
            when the earliest and latest collection are in the same Monday to Sunday week, and the
            different week setting when the latest collection is in a later week. When this is what
            releases the job, it's still held for that week's minimum hold.
          </li>
          <li>
            <b>Reschedules.</b> When a job is rescheduled with Reschedule Task, a held job's
            collection time is changed, or a route is handed back or aborted and split back into its
            jobs, the <b>Reschedules</b> short timer and late release are used instead, and the
            earliest collection minimum hold is skipped. Everything else is shared. A job that's
            already out of hold stays out when its dates change.
          </li>
        </ol>
        <p>
          <b>Example:</b> booked Monday 13:00 to collect Tuesday 13:00 to Friday 13:00. Friday's row
          releases on Wednesday, but with the same week earliest collection at 1200 minutes (20
          hours) the job comes out on Monday at 17:00 instead.
        </p>
        <p class="mb-0">
          Saved changes apply to new jobs straight away, and to held jobs when their collection time
          is changed. Jobs already held keep their release time. Task types that are set not to hold
          on the Service Type On Hold screen skip steps 1 to 3 and are held for the minimum on-hold
          time in System Configuration only.
        </p>
      </div>
    </app-alert-panel>

    <ng-container *ngTemplateOutlet="problems; context: { $implicit: errors.page }"></ng-container>

    <div class="card">
      <h2 class="mt-0">Release windows</h2>
      <p>
        The window a job is released in, chosen by the day it is being collected. A job booked on or
        after its release day has already missed the window and takes the late release below
        instead. Changes apply to new jobs, and to held jobs whose collection time is later changed.
      </p>

      <p-table [value]="settings.releaseWindows">
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 16%">Collection day</th>
            <th style="width: 20%">Releases on</th>
            <th style="width: 18%">From</th>
            <th style="width: 18%">To</th>
            <th style="width: 12%">Length</th>
            <th style="width: 16%" class="text-center">Randomise</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-window>
          <tr>
            <td [class.text-danger]="errors[rowKey(window.pickupDay)]">
              <i *ngIf="errors[rowKey(window.pickupDay)]" class="fa fa-exclamation-circle mr-1"></i>
              {{ dayName(window.pickupDay) }}
            </td>
            <td>
              <p-dropdown
                [options]="dayOptions"
                [(ngModel)]="window.releaseDay"
                (ngModelChange)="clearErrors(rowKey(window.pickupDay))"
                appendTo="body"
                styleClass="w-full"
              ></p-dropdown>
            </td>
            <td>
              <p-calendar
                [(ngModel)]="window.startTime"
                (ngModelChange)="clearErrors(rowKey(window.pickupDay))"
                [timeOnly]="true"
                styleClass="w-full"
                inputStyleClass="w-full"
              ></p-calendar>
            </td>
            <td>
              <p-calendar
                [(ngModel)]="window.endTime"
                (ngModelChange)="clearErrors(rowKey(window.pickupDay))"
                [timeOnly]="true"
                styleClass="w-full"
                inputStyleClass="w-full"
              ></p-calendar>
            </td>
            <td>{{ windowLength(window) }}</td>
            <td class="text-center">
              <p-inputSwitch
                [(ngModel)]="window.randomise"
                pTooltip="Spread releases across this window instead of releasing every job at the start. Late and overnight releases are always spread."
              ></p-inputSwitch>
            </td>
          </tr>
        </ng-template>
      </p-table>

      <ng-container
        *ngTemplateOutlet="problems; context: { $implicit: windowErrors }"
      ></ng-container>
    </div>

    <div class="card">
      <h2 class="mt-0">Short timer</h2>
      <p>Used when a job has fewer business days to its collection than the threshold.</p>

      <div class="formgrid grid">
        <div class="field col-12 md:col-4">
          <label>Business days for a release window</label>
          <input
            pInputText
            type="number"
            min="1"
            max="5"
            class="w-full"
            [(ngModel)]="settings.configuration.regime2MinimumBusinessDays"
            (ngModelChange)="clearErrors('shortTimer')"
          />
          <small>Fewer than this and the job gets the short timer.</small>
        </div>
        <div class="field col-12 md:col-4">
          <label>Hold for, collecting today (minutes)</label>
          <input
            pInputText
            type="number"
            min="0"
            class="w-full"
            [(ngModel)]="settings.configuration.sameDayProcessingMinutes"
            (ngModelChange)="clearErrors('shortTimer')"
          />
        </div>
        <div class="field col-12 md:col-4">
          <label>Hold for, collecting another day (minutes)</label>
          <input
            pInputText
            type="number"
            min="0"
            class="w-full"
            [(ngModel)]="settings.configuration.nextDayProcessingMinutes"
            (ngModelChange)="clearErrors('shortTimer')"
          />
        </div>
      </div>

      <h3>Reschedules</h3>
      <p>
        Used instead when a job is rescheduled, its collection time is changed, or a route is handed
        back.
      </p>

      <div class="formgrid grid">
        <div class="field col-12 md:col-4 md:col-offset-4">
          <label>Hold for, collecting today (minutes)</label>
          <input
            pInputText
            type="number"
            min="0"
            class="w-full"
            [(ngModel)]="settings.configuration.rescheduleSameDayProcessingMinutes"
            (ngModelChange)="clearErrors('shortTimer')"
          />
        </div>
        <div class="field col-12 md:col-4">
          <label>Hold for, collecting another day (minutes)</label>
          <input
            pInputText
            type="number"
            min="0"
            class="w-full"
            [(ngModel)]="settings.configuration.rescheduleNextDayProcessingMinutes"
            (ngModelChange)="clearErrors('shortTimer')"
          />
        </div>
      </div>

      <ng-container
        *ngTemplateOutlet="problems; context: { $implicit: errors.shortTimer }"
      ></ng-container>
    </div>

    <div class="card">
      <h2 class="mt-0">Late release</h2>
      <p>Used when a job is booked on or after the release day for its collection day.</p>

      <div class="formgrid grid">
        <div class="field col-12 md:col-4">
          <label>From</label>
          <p-calendar
            [(ngModel)]="settings.configuration.lateReleaseStartTime"
            (ngModelChange)="clearErrors('lateRelease')"
            [timeOnly]="true"
            styleClass="w-full"
            inputStyleClass="w-full"
          ></p-calendar>
        </div>
        <div class="field col-12 md:col-4">
          <label>To</label>
          <p-calendar
            [(ngModel)]="settings.configuration.lateReleaseEndTime"
            (ngModelChange)="clearErrors('lateRelease')"
            [timeOnly]="true"
            styleClass="w-full"
            inputStyleClass="w-full"
          ></p-calendar>
        </div>
        <div class="field col-12 md:col-4">
          <label>Minimum hold (minutes)</label>
          <input
            pInputText
            type="number"
            min="0"
            class="w-full"
            [(ngModel)]="settings.configuration.lateReleaseMinimumHoldMinutes"
            (ngModelChange)="clearErrors('lateRelease')"
          />
          <small>A job booked late still holds this long, even past the window.</small>
        </div>
      </div>

      <h3>Reschedules</h3>
      <p>
        Used instead when a job is rescheduled, its collection time is changed, or a route is handed
        back. A window that has already passed releases straight away, but never overnight.
      </p>

      <div class="formgrid grid">
        <div class="field col-12 md:col-4">
          <label>From</label>
          <p-calendar
            [(ngModel)]="settings.configuration.rescheduleLateReleaseStartTime"
            (ngModelChange)="clearErrors('lateRelease')"
            [timeOnly]="true"
            styleClass="w-full"
            inputStyleClass="w-full"
          ></p-calendar>
        </div>
        <div class="field col-12 md:col-4">
          <label>To</label>
          <p-calendar
            [(ngModel)]="settings.configuration.rescheduleLateReleaseEndTime"
            (ngModelChange)="clearErrors('lateRelease')"
            [timeOnly]="true"
            styleClass="w-full"
            inputStyleClass="w-full"
          ></p-calendar>
        </div>
        <div class="field col-12 md:col-4">
          <label>Minimum hold (minutes)</label>
          <input
            pInputText
            type="number"
            min="0"
            class="w-full"
            [(ngModel)]="settings.configuration.rescheduleLateReleaseMinimumHoldMinutes"
            (ngModelChange)="clearErrors('lateRelease')"
          />
        </div>
      </div>

      <ng-container
        *ngTemplateOutlet="problems; context: { $implicit: errors.lateRelease }"
      ></ng-container>
    </div>

    <div class="card">
      <h2 class="mt-0">Overnight</h2>
      <p>
        A release from the windows or the late release that lands between the cutoff and the morning
        window is moved into the morning window. The short timer is not affected.
      </p>

      <div class="formgrid grid">
        <div class="field col-12 md:col-4">
          <label>Cutoff</label>
          <p-calendar
            [(ngModel)]="settings.configuration.overnightCutoffTime"
            (ngModelChange)="clearErrors('overnight')"
            [timeOnly]="true"
            styleClass="w-full"
            inputStyleClass="w-full"
          ></p-calendar>
        </div>
        <div class="field col-12 md:col-4">
          <label>Morning window from</label>
          <p-calendar
            [(ngModel)]="settings.configuration.overnightResumeStartTime"
            (ngModelChange)="clearErrors('overnight')"
            [timeOnly]="true"
            styleClass="w-full"
            inputStyleClass="w-full"
          ></p-calendar>
        </div>
        <div class="field col-12 md:col-4">
          <label>Morning window to</label>
          <p-calendar
            [(ngModel)]="settings.configuration.overnightResumeEndTime"
            (ngModelChange)="clearErrors('overnight')"
            [timeOnly]="true"
            styleClass="w-full"
            inputStyleClass="w-full"
          ></p-calendar>
        </div>
      </div>

      <ng-container
        *ngTemplateOutlet="problems; context: { $implicit: errors.overnight }"
      ></ng-container>
    </div>

    <div class="card">
      <h2 class="mt-0">Earliest collection</h2>
      <p>
        Caps every release, whatever the windows above work out to. Same week is when the earliest
        and latest collection fall in the same Monday to Sunday week. A negative number lets the job
        stay held after the earliest collection, e.g. -1440 for up to a day. When this is what
        releases a job, it is still held for the minimum hold. Reschedules skip it.
      </p>

      <div class="formgrid grid">
        <div class="field col-12 md:col-6">
          <label>Out of hold before collection by (minutes): same week</label>
          <input
            pInputText
            type="number"
            min="-10080"
            max="10080"
            class="w-full"
            [(ngModel)]="settings.configuration.earliestPickupMinimumNoticeSameWeekMinutes"
            (ngModelChange)="clearErrors('earliestCollection')"
          />
        </div>
        <div class="field col-12 md:col-6">
          <label>Minimum hold (minutes): same week</label>
          <input
            pInputText
            type="number"
            min="0"
            class="w-full"
            [(ngModel)]="settings.configuration.earliestPickupMinimumHoldSameWeekMinutes"
            (ngModelChange)="clearErrors('earliestCollection')"
          />
        </div>
        <div class="field col-12 md:col-6">
          <label>Out of hold before collection by (minutes): different week</label>
          <input
            pInputText
            type="number"
            min="-10080"
            max="10080"
            class="w-full"
            [(ngModel)]="settings.configuration.earliestPickupMinimumNoticeDifferentWeekMinutes"
            (ngModelChange)="clearErrors('earliestCollection')"
          />
        </div>
        <div class="field col-12 md:col-6">
          <label>Minimum hold (minutes): different week</label>
          <input
            pInputText
            type="number"
            min="0"
            class="w-full"
            [(ngModel)]="settings.configuration.earliestPickupMinimumHoldDifferentWeekMinutes"
            (ngModelChange)="clearErrors('earliestCollection')"
          />
        </div>
      </div>

      <ng-container
        *ngTemplateOutlet="problems; context: { $implicit: errors.earliestCollection }"
      ></ng-container>
    </div>
  </ng-container>
</div>

<ng-template #problems let-list>
  <app-alert-panel
    *ngIf="list"
    severity="error"
    heading="Unable to save changes"
    class="block mt-3"
  >
    <div *ngFor="let problem of list">{{ problem }}</div>
  </app-alert-panel>
</ng-template>

<p-confirmDialog
  key="job-hold-save"
  header="Confirmation"
  icon="fa-icon-circle"
  [style]="{ width: '425px' }"
  acceptLabel="Save"
  rejectLabel="Cancel"
></p-confirmDialog>
